import { DateTime } from 'luxon';

import { formatNumber } from '@/globals';

interface ISpendSteeringSummaryAPI {
  confirmed_at_local: string;
  owner_id?: string;
  deal_id?: string;
  imps?: number;
  spend?: number;
}

interface ISpendSteeringSummaryEntry {
  confirmedAtLocal: string;
  dimensionValue: string;
  totalDelivery: number;
}

interface ISpendSteeringSummaryMapping {
  confirmedAtLocal: string;
  totalDelivery: number;
}

interface ISpendSteeringSummaryCumulativeMapping {
  confirmedAtLocal: string;
  totalDelivery: number;
  cumulativeWeight: number;
}

export default class SpendSteeringSummary {
  public entries = new Map<string, ISpendSteeringSummaryMapping[]>();
  public cumulativeSpendByDate = new Map<string, number>();
  public timeFrame: DateTime[];
  constructor(
    data: ISpendSteeringSummaryEntry[],
    public startDate: string,
    public endDate: string,
    public granularity: string = 'DAILY',
  ) {
    this.timeFrame = this.calculateTimeFrame(data);
    this.processDailyGranularity(data);
    this.cumulativeSpendByDate = this.calculateCumulativeSpendByDate();
  }

  private calculateTimeFrame(data: ISpendSteeringSummaryEntry[]): DateTime[] {
    const uniqueTimeFrame = [...new Set(data.map((entry) => entry.confirmedAtLocal))];
    // Sort the dates in ascending order
    const sortedTimeFrame: DateTime[] = uniqueTimeFrame
      .map((date) => DateTime.fromISO(date))
      .sort((a, b) => a.toMillis() - b.toMillis());
    // Pad the time frame so that it includes all dates within flight (providing they fall within flight)
    this.padTimeFrame(sortedTimeFrame);
    // Fill any gaps within the time frame depending on granularity
    this.fillTimeFrame(sortedTimeFrame);
    return sortedTimeFrame;
  }

  private fillTimeFrame(dates: DateTime[]) {
    for (let i = 0; i < dates.length - 1; i++) {
      const currentDate = dates[i];
      const nextDate = dates[i + 1];
      // Calculate difference between dates
      const interval = nextDate.toMillis() - currentDate.toMillis();
      const msPerGranularity = this.granularity === 'DAILY' ? 1000 * 60 * 60 * 24 : 1000 * 60 * 60;
      const diff = interval / msPerGranularity;
      // If there is more than a day difference between two dates, add a new date in between
      if (diff > 1) {
        const newDate = currentDate.plus({ milliseconds: msPerGranularity });
        dates.splice(i + 1, 0, newDate);
      }
    }
  }

  /**
   * We need to ensure that the timeframe includes all dates within flight (providing they fall within flight)
   *
   * @param timeFrame - Assumed sorted range of dates
   * @private
   */
  private padTimeFrame(timeFrame: DateTime[]) {
    // Added `.startOf('day')` to values returned from the server just to be safe (should be in ISO date format)
    if (timeFrame.length === 0) {
      return;
    }

    // Add the first date to the time frame if it is not already included
    const firstDate = DateTime.fromISO(this.startDate).startOf('day');
    if (timeFrame[0].startOf('day').toMillis() !== firstDate.toMillis()) {
      timeFrame.unshift(firstDate);
    }

    // Add the last date of flight to the time frame if not already included (providing it is not in the future)
    const today = DateTime.now().startOf('day');
    const endDate = DateTime.fromISO(this.endDate).startOf('day');
    const lastDate = endDate.toMillis() < today.toMillis() ? endDate : today;
    if (timeFrame[timeFrame.length - 1].startOf('day').toMillis() !== lastDate.toMillis()) {
      timeFrame.push(lastDate);
    }

    // If the granularity is hourly, we need to ensure that the timeframe includes all hours within the range
    if (this.granularity === 'HOURLY') {
      this.padHourlyTimeFrame(timeFrame);
    }
  }

  /**
   * Pads timeframe to include the start date at 00:00 and end date at 23:00
   *
   * @param timeFrame - Assumed sorted range of dates
   * @private
   */
  private padHourlyTimeFrame(timeFrame: DateTime[]) {
    if (timeFrame[0].hour !== 0) {
      const firstDate = timeFrame[0].startOf('day');
      timeFrame.unshift(firstDate);
    }
    if (timeFrame[timeFrame.length - 1].hour !== 23) {
      const lastDate = timeFrame[timeFrame.length - 1].endOf('day');
      timeFrame.push(lastDate);
    }
  }

  processDailyGranularity(data: ISpendSteeringSummaryEntry[]) {
    const dimensionValues = new Set(data.map((entry) => entry.dimensionValue));
    dimensionValues.forEach((dimension) => {
      if (!this.entries.has(dimension)) {
        this.entries.set(dimension, []);
      }
      this.timeFrame.forEach((date) => {
        const dateStr = date.toISO();
        this.entries.get(dimension).push({
          confirmedAtLocal: dateStr,
          totalDelivery:
            data.find((entry) => entry.dimensionValue === dimension && entry.confirmedAtLocal === dateStr)
              ?.totalDelivery ?? 0,
        });
      });
    });
  }

  private calculateCumulativeSpendByDate(): Map<string, number> {
    let cumulativeSpend = 0;
    const totalSpendByDate = [...this.entries.values()]
      .flat()
      .reduce(
        (acc, entry) => acc.set(entry.confirmedAtLocal, (acc.get(entry.confirmedAtLocal) || 0) + entry.totalDelivery),
        new Map<string, number>(),
      );
    return new Map(
      [...totalSpendByDate]
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .map(([date, spend]) => {
          cumulativeSpend += spend;
          return [date, cumulativeSpend];
        }),
    );
  }

  getSummaryByDimension(dimension: string): ISpendSteeringSummaryMapping[] {
    return this.entries.get(dimension) || [];
  }

  getTotalDeliveryByDimension(dimension: string): number {
    return this.getSummaryByDimension(dimension).reduce((acc, entry) => acc + entry.totalDelivery, 0);
  }

  getPercentageByDimension(dimension: string): string {
    const totalDeliveryForDimension = this.getTotalDeliveryByDimension(dimension);
    const totalDeliveryForAllDimensions = Array.from(this.entries.values())
      .flat()
      .reduce((acc, entry) => acc + entry.totalDelivery, 0);
    const percentage =
      totalDeliveryForAllDimensions === 0 ? 0 : totalDeliveryForDimension / totalDeliveryForAllDimensions;
    return formatNumber(percentage, '0.00%');
  }

  getCumulativeSummaryByDimension(dimension: string): ISpendSteeringSummaryCumulativeMapping[] {
    const dimensionSummary = this.getSummaryByDimension(dimension);
    if (dimensionSummary.length === 0) {
      return [];
    }
    return dimensionSummary.reduce((acc, entry) => {
      const totalDelivery = entry.totalDelivery + (acc[acc.length - 1]?.totalDelivery ?? 0);
      const cumulativeWeight = this.cumulativeSpendByDate.get(entry.confirmedAtLocal);
      acc.push({
        confirmedAtLocal: entry.confirmedAtLocal,
        totalDelivery,
        cumulativeWeight: cumulativeWeight === 0 ? 0 : totalDelivery / cumulativeWeight,
      });
      return acc;
    }, []);
  }

  static fromAPI(
    data: ISpendSteeringSummaryAPI[],
    startDate: string,
    endDate: string,
    granularity: string,
  ): SpendSteeringSummary {
    return new SpendSteeringSummary(
      data.map(
        (entry: ISpendSteeringSummaryAPI): ISpendSteeringSummaryEntry => ({
          confirmedAtLocal: DateTime.fromISO(entry?.confirmed_at_local).toISO(),
          dimensionValue: entry?.deal_id || entry?.owner_id?.toString(),
          totalDelivery: entry?.imps || entry?.spend,
        }),
      ),
      startDate,
      endDate,
      granularity,
    );
  }
}
